import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { updateUserEmail } from "@/domains/people";
import { useAuth } from "@/providers/auth-provider";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User, Users, Mail, Shield, Clock, Monitor, Loader2, BarChart3, Briefcase, Calendar as CalendarIcon, DollarSign, Save, Pause } from "lucide-react";
import { ManageTeamDialog } from "@/pages/users/components/manage-team-dialog";
import { AssignProjectsDialog } from "@/pages/users/components/assign-projects-dialog";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { format, subDays } from 'date-fns';
import { getQatarDayRange, getTodayQatar } from '@/utils/timezone';
import { formatEmployeeId } from '@/lib/employee-display';

type UserProfile = {
  id: string;
  email: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
  is_active: boolean | null;
  last_activity: string | null;
  agent_version?: string | null;
  paused_at: string | null;
  paused_by: string | null;
  pause_reason: string | null;
  position: string | null;
  joining_date: string | null;
  salary_type: string | null;
  salary_amount: number | null;
};

type EmploymentForm = {
  position: string;
  joining_date: string;
  salary_type: '' | 'hourly' | 'monthly';
  salary_amount: string;
};

function getRoleBadgeColor(role: string) {
  switch (role) {
    case "admin": return "destructive";
    case "manager": return "outline";
    default: return "secondary";
  }
}

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const { toast } = useToast();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [employmentDialogOpen, setEmploymentDialogOpen] = useState(false);
  const [savingEmployment, setSavingEmployment] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [employmentForm, setEmploymentForm] = useState<EmploymentForm>({
    position: "",
    joining_date: "",
    salary_type: "",
    salary_amount: "",
  });
  const [employmentErrors, setEmploymentErrors] = useState<Partial<Record<keyof EmploymentForm, string>>>({});
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [projectsDialogOpen, setProjectsDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [confirmingEmail, setConfirmingEmail] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [pausingUser, setPausingUser] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [updatingRole, setUpdatingRole] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [profileStats, setProfileStats] = useState({
    workedMinutes: 0,
    idleMinutes: 0,
    loading: false
  });

  const canEdit = userDetails?.role === "admin" || userDetails?.is_super_admin;
  // Salary amount is sensitive HR/payroll data. Restrict to org admins, super admins,
  // and the dedicated `hr_admin` role. Other admin/manager roles can see Joining Date,
  // Position, Employee ID, and Salary Type but never the amount.
  const canSeeSalaryAmount =
    userDetails?.is_super_admin === true ||
    userDetails?.is_org_admin === true ||
    userDetails?.role === 'hr_admin';

  useEffect(() => {
    if (userId) fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("users")
        .select(
          "id, email, full_name, role, avatar_url, is_active, last_activity, paused_at, paused_by, pause_reason"
        )
        .eq("id", userId!)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setUser(null);
        return;
      }

      const employmentFields = {
        position: null as string | null,
        joining_date: null as string | null,
        salary_type: null as string | null,
        salary_amount: null as number | null,
      };

      try {
        const employmentSelect = canSeeSalaryAmount
          ? "position, joining_date, salary_type, salary_amount"
          : "position, joining_date, salary_type";
        const { data: employmentData, error: employmentError } = await supabase
          .from("users")
          .select(employmentSelect)
          .eq("id", userId!)
          .maybeSingle();

        if (employmentError) {
          console.error("Error fetching employment fields:", employmentError);
        } else if (employmentData) {
          const employment = employmentData as unknown as Pick<
            UserProfile,
            "position" | "joining_date" | "salary_type" | "salary_amount"
          >;
          employmentFields.position = employment.position ?? null;
          employmentFields.joining_date = employment.joining_date ?? null;
          employmentFields.salary_type = employment.salary_type ?? null;
          employmentFields.salary_amount = canSeeSalaryAmount
            ? employment.salary_amount ?? null
            : null;
        }
      } catch (employmentError) {
        console.error("Error fetching employment fields:", employmentError);
      }

      // Get latest agent version from screenshots
      const { data: versionData, error: versionError } = await supabase
        .from("screenshots")
        .select("agent_version")
        .eq("user_id", userId!)
        .not("agent_version", "is", null)
        .order("captured_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (versionError) {
        console.error("Error fetching agent version:", versionError);
      }

      const profile = data as unknown as UserProfile;
      setUser({
        ...profile,
        ...employmentFields,
        agent_version: versionData?.agent_version ?? null,
      });
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Could not load user profile.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileStats = async () => {
    if (!userId) return;
    setProfileStats(s => ({ ...s, loading: true }));
    try {
      const fromStr = format(dateRange.from, 'yyyy-MM-dd');
      const toStr = format(dateRange.to, 'yyyy-MM-dd');
      const { start: startISO } = getQatarDayRange(fromStr);
      const { end: endISO } = getQatarDayRange(toStr);

      const { data: logs } = await supabase
        .from('time_logs')
        .select('start_time, end_time, deducted_seconds')
        .eq('user_id', userId)
        .gte('start_time', startISO)
        .lte('start_time', endISO);

      let workedMinutes = 0;
      (logs || []).forEach(log => {
        if (!log.start_time || !log.end_time) return;
        const ms = new Date(log.end_time).getTime() - new Date(log.start_time).getTime();
        const deducted = (log.deducted_seconds || 0) * 1000;
        workedMinutes += Math.max(0, ms - deducted) / 60000;
      });

      const { count: idleCount } = await supabase
        .from('screenshots')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('captured_at', startISO)
        .lte('captured_at', endISO)
        .lt('activity_percent', 20);

      const idleMinutes = (idleCount || 0) * 5;

      setProfileStats({
        workedMinutes: Math.round(workedMinutes),
        idleMinutes,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching profile stats:', err);
      setProfileStats(s => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { fetchProfileStats(); }, [userId, dateRange]);

  const handleToggleActive = async () => {
    if (!user || !canEdit) return;
    try {
      setToggling(true);
      const newStatus = !user.is_active;
      const { error } = await supabase
        .from("users")
        .update({ is_active: newStatus })
        .eq("id", user.id);

      if (error) throw error;

      setUser(prev => prev ? { ...prev, is_active: newStatus } : prev);
      toast({
        title: newStatus ? "User Activated" : "User Deactivated",
        description: `${user.full_name} is now ${newStatus ? "active" : "inactive"}.`,
      });
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Could not update user status.", variant: "destructive" });
    } finally {
      setToggling(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!user || !selectedRole) return;
    try {
      setUpdatingRole(true);
      const { error } = await supabase
        .from("users")
        .update({ role: selectedRole })
        .eq("id", user.id);

      if (error) throw error;

      setUser(prev => prev ? { ...prev, role: selectedRole } : prev);
      toast({
        title: "Role Updated",
        description: `${user.full_name} is now ${selectedRole.replace("_", " ")}.`,
      });
      setRoleDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Could not update role.", variant: "destructive" });
    } finally {
      setUpdatingRole(false);
    }
  };

  const openEmailDialog = () => {
    if (!user || !canEdit) return;
    setEmailValue(user.email);
    setEmailDialogOpen(true);
  };

  const handleUpdateEmail = async () => {
    if (!user || !canEdit) return;
    const nextEmail = emailValue.trim();
    if (!nextEmail) {
      toast({ title: "Email is required", variant: "destructive" });
      return;
    }

    try {
      setUpdatingEmail(true);
      const updatedUser = await updateUserEmail(user.id, nextEmail);
      const updatedEmail = updatedUser.email || nextEmail;

      setUser(prev => prev ? { ...prev, email: updatedEmail } : prev);
      toast({
        title: "Email updated",
        description: `Email has been changed to ${updatedEmail}.`,
      });
      setEmailDialogOpen(false);
    } catch (err: any) {
      toast({
        title: "Error updating email",
        description: err?.message || "Could not update email.",
        variant: "destructive",
      });
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!user || !canEdit) return;
    try {
      setConfirmingEmail(true);
      const { data, error } = await supabase.functions.invoke("resend-confirmation", {
        body: { userId: user.id, email: user.email },
      });

      if (error) {
        const message = typeof data?.error === "string" ? data.error : error.message;
        throw new Error(message);
      }

      toast({
        title: "Email confirmed",
        description: `${user.email} has been confirmed successfully.`,
      });
    } catch (err: any) {
      toast({
        title: "Error confirming email",
        description: err?.message || "Could not confirm email.",
        variant: "destructive",
      });
    } finally {
      setConfirmingEmail(false);
    }
  };

  const handlePauseUser = async () => {
    if (!user || !canEdit) return;
    if (!pauseReason.trim()) {
      toast({ title: "Reason is required", variant: "destructive" });
      return;
    }

    try {
      setPausingUser(true);
      const pausedAt = new Date().toISOString();
      const { error } = await supabase
        .from("users")
        .update({
          is_active: false,
          paused_at: pausedAt,
          paused_by: userDetails?.id,
          pause_reason: pauseReason.trim(),
        } as any)
        .eq("id", user.id);

      if (error) throw error;

      setUser(prev => prev ? {
        ...prev,
        is_active: false,
        paused_at: pausedAt,
        paused_by: userDetails?.id ?? null,
        pause_reason: pauseReason.trim(),
      } : prev);
      toast({
        title: "User paused",
        description: `${user.full_name} has been paused successfully.`,
      });
      setPauseDialogOpen(false);
      setPauseReason("");
    } catch (err: any) {
      toast({
        title: "Error pausing user",
        description: err?.message || "Could not pause user.",
        variant: "destructive",
      });
    } finally {
      setPausingUser(false);
    }
  };

  const handleActionClick = (label: string) => {
    if (label === "Hours") {
      document.getElementById("work-statistics-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (!canEdit) {
      toast({
        title: "No permission",
        description: "You do not have permission to perform this action.",
        variant: "destructive",
      });
      return;
    }

    switch (label) {
      case "Role":
        setSelectedRole(user.role);
        setRoleDialogOpen(true);
        break;
      case "Email":
        openEmailDialog();
        break;
      case "Confirm":
        handleResendConfirmation();
        break;
      case "Projects":
        setProjectsDialogOpen(true);
        break;
      case "Pause":
        setPauseReason("");
        setPauseDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user || !canEdit) return;

    try {
      setSendingPasswordReset(true);
      const { data, error } = await supabase.functions.invoke("resend-confirmation", {
        body: {
          action: "password-reset",
          userId: user.id,
          email: user.email,
        },
      });

      if (error) {
        const message = typeof data?.error === "string" ? data.error : error.message;
        throw new Error(message);
      }

      toast({
        title: "Password reset email sent",
        description: `Password reset instructions sent to ${user.email}.`,
        duration: 6000,
      });
      setPasswordResetDialogOpen(false);
    } catch (err: unknown) {
      const description =
        err instanceof Error ? err.message : "Could not send password reset email.";
      toast({
        title: "Error resetting password",
        description,
        variant: "destructive",
      });
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const openEmploymentDialog = () => {
    if (!user) return;
    setEmploymentForm({
      position: user.position ?? "",
      joining_date: user.joining_date ?? "",
      salary_type: (user.salary_type === 'hourly' || user.salary_type === 'monthly')
        ? user.salary_type
        : "",
      salary_amount: user.salary_amount != null ? String(user.salary_amount) : "",
    });
    setEmploymentErrors({});
    setEmploymentDialogOpen(true);
  };

  const validateEmploymentForm = (
    form: EmploymentForm,
  ): Partial<Record<keyof EmploymentForm, string>> => {
    const errors: Partial<Record<keyof EmploymentForm, string>> = {};

    if (!form.salary_type) {
      errors.salary_type = "Salary Type is required";
    }

    if (form.joining_date) {
      // Compare against the current Qatar date to keep behavior consistent
      // with the rest of the app (all date filters use Qatar timezone).
      const today = getTodayQatar();
      if (form.joining_date > today) {
        errors.joining_date = "Joining Date cannot be in the future";
      }
    }

    if (form.salary_amount.trim() !== "") {
      const num = Number(form.salary_amount);
      if (Number.isNaN(num)) {
        errors.salary_amount = "Salary Amount must be a number";
      } else if (num < 0) {
        errors.salary_amount = "Salary Amount cannot be negative";
      }
    }

    return errors;
  };

  const handleSaveEmployment = async () => {
    if (!user || !canEdit) return;

    const errors = validateEmploymentForm(employmentForm);
    setEmploymentErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setSavingEmployment(true);

      const payload: Record<string, unknown> = {
        position: employmentForm.position.trim() || null,
        joining_date: employmentForm.joining_date || null,
        salary_type: employmentForm.salary_type || null,
      };

      // Only org-admin/super-admin/hr_admin may write the salary amount.
      // For other admins (e.g. plain `admin`), preserve the existing value.
      if (canSeeSalaryAmount) {
        payload.salary_amount = employmentForm.salary_amount.trim() === ""
          ? null
          : Number(employmentForm.salary_amount);
      }

      const { error } = await supabase
        .from("users")
        .update(payload)
        .eq("id", user.id);

      if (error) throw error;

      setUser(prev => prev ? {
        ...prev,
        position: payload.position as string | null,
        joining_date: payload.joining_date as string | null,
        salary_type: (payload.salary_type as string | null) ?? null,
        salary_amount: canSeeSalaryAmount
          ? (payload.salary_amount as number | null) ?? null
          : prev.salary_amount,
      } : prev);

      toast({
        title: "Employment details updated",
        description: `Saved Employment & Salary Details for ${user.full_name}.`,
      });
      setEmploymentDialogOpen(false);
    } catch (err: unknown) {
      console.error(err);
      // Surface the DB-level CHECK constraint failures to the user with a
      // specific, friendly message; otherwise keep the generic copy.
      const message: string =
        err instanceof Error ? err.message : (err as { message?: string })?.message || "";
      let description = "Could not save employment details.";
      if (message.includes("users_joining_date_not_future_chk")) {
        description = "Joining Date cannot be in the future.";
      } else if (message.includes("users_salary_type_chk")) {
        description = "Salary Type must be Hourly or Monthly.";
      } else if (message.includes("users_salary_amount_nonneg_chk")) {
        description = "Salary Amount cannot be negative.";
      }
      toast({ title: "Error", description, variant: "destructive" });
    } finally {
      setSavingEmployment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/users")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Users
        </Button>
        <p className="text-muted-foreground">User not found.</p>
      </div>
    );
  }

  const isActive = user.is_active !== false;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate("/users")} className="mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Users
      </Button>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name} className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center text-2xl font-semibold text-muted-foreground">
                {user.full_name?.charAt(0)?.toUpperCase() ?? "?"}
              </div>
            )}
            <div>
              <CardTitle className="text-xl">{user.full_name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getRoleBadgeColor(user.role) as any}>{user.role}</Badge>
                <Badge variant={isActive ? "default" : "secondary"}>
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Employee ID (derived from user id while live schema lacks users.staff_id) */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Employee ID</p>
              <p className="font-mono font-semibold">
                {formatEmployeeId(null, user.id)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            {[
              { label: "Hours", color: "default" },
              { label: "Role", color: "default" },
              { label: "Email", color: "default" },
              { label: "Confirm", color: "default" },
              { label: "Projects", color: "default" },
              { label: "Pause", color: "destructive" },
            ].map(({ label, color }) => (
              <Button
                key={label}
                variant={color === "destructive" ? "destructive" : "outline"}
                size="sm"
                onClick={() => handleActionClick(label)}
                disabled={(label !== "Hours" && !canEdit) || (label === "Confirm" && confirmingEmail)}
              >
                {label === "Confirm" && confirmingEmail && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {label}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPasswordResetDialogOpen(true)}
              disabled={!canEdit || !user.email}
            >
              Password
            </Button>
            {user.role === "team_leader" && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setTeamDialogOpen(true)}
              >
                <Users className="h-4 w-4 mr-1" />
                Team
              </Button>
            )}
          </div>

          {/* Role Change Dialog */}
          <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Role for {user.full_name}</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateRole} disabled={updatingRole || selectedRole === user.role}>
                  {updatingRole && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Role
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Email Dialog */}
          <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Email for {user.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-4">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={updatingEmail}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateEmail} disabled={updatingEmail || emailValue.trim() === user.email}>
                  {updatingEmail && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Email
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Password Reset Dialog */}
          <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset Password for {user.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="font-medium">{user.full_name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>

                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  A secure password reset email will be sent to this user. The user can open the email link and create a new password.
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPasswordResetDialogOpen(false)}
                    disabled={sendingPasswordReset}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSendPasswordReset}
                    disabled={sendingPasswordReset}
                  >
                    {sendingPasswordReset && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Mail className="h-4 w-4 mr-2" />
                    Send Reset Email
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manage Team Dialog */}
          <ManageTeamDialog
            open={teamDialogOpen}
            onOpenChange={setTeamDialogOpen}
            teamLeaderId={user.id}
            teamLeaderName={user.full_name}
          />

          {/* Assign Projects Dialog */}
          <AssignProjectsDialog
            open={projectsDialogOpen}
            onOpenChange={setProjectsDialogOpen}
            userId={user.id}
            userName={user.full_name}
            currentUserId={userDetails?.id}
            organizationId={userDetails?.organization_id}
            isSuperAdmin={userDetails?.is_super_admin}
          />

          {/* Pause User Dialog */}
          <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Pause User Account</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <p className="font-medium">{user.full_name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <p className="text-sm text-orange-600">
                    This will deactivate the user and hide them from active reports.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pause-reason">Reason for pausing</Label>
                  <Textarea
                    id="pause-reason"
                    value={pauseReason}
                    onChange={(e) => setPauseReason(e.target.value)}
                    placeholder="e.g. employee left, on leave, temporary pause"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPauseDialogOpen(false)} disabled={pausingUser}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handlePauseUser} disabled={pausingUser}>
                  {pausingUser ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pause className="h-4 w-4 mr-2" />}
                  Pause User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Email */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="font-medium">{user.email}</p>
            </div>
          </div>

          {/* Role */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Role</p>
              <p className="font-medium capitalize">{user.role}</p>
            </div>
          </div>

          {/* Last Activity */}
          {user.last_activity && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Last Activity</p>
                <p className="font-medium">{new Date(user.last_activity).toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Agent Version */}
          {user.agent_version && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Monitor className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Agent Version</p>
                <p className="font-medium">{user.agent_version}</p>
              </div>
            </div>
          )}

          {/* Pause Info */}
          {user.paused_at && (
            <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
              <p className="font-semibold">User is paused</p>
              {user.pause_reason && <p className="mt-1">{user.pause_reason}</p>}
              <p className="text-xs mt-1">{new Date(user.paused_at).toLocaleString()}</p>
            </div>
          )}

          {/* Active / Inactive Toggle */}
          {canEdit && (
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label htmlFor="active-toggle" className="text-base font-semibold">
                  Account Status
                </Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {isActive
                    ? "User is active and visible in reports"
                    : "User is inactive and hidden from reports"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {toggling && <Loader2 className="h-4 w-4 animate-spin" />}
                <Switch
                  id="active-toggle"
                  checked={isActive}
                  onCheckedChange={handleToggleActive}
                  disabled={toggling}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employment & Salary Details */}
      <Card className="mt-6" id="work-statistics-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Employment &amp; Salary Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
              <CalendarIcon className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Joining Date</p>
                <p className="font-medium">
                  {user.joining_date
                    ? format(new Date(user.joining_date + 'T00:00:00'), 'MMM d, yyyy')
                    : '—'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
              <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Position</p>
                <p className="font-medium">{user.position?.trim() || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
              <DollarSign className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Salary Type</p>
                <p className="font-medium capitalize">{user.salary_type || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
              <DollarSign className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Salary Amount</p>
                <p className="font-medium">
                  {canSeeSalaryAmount
                    ? (user.salary_amount != null ? user.salary_amount : '—')
                    : <span className="text-muted-foreground italic">Restricted</span>}
                </p>
                {!canSeeSalaryAmount && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Visible to admin / HR / payroll roles only.
                  </p>
                )}
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="flex justify-end mt-4">
              <Button variant="outline" size="sm" onClick={openEmploymentDialog}>
                <Briefcase className="h-4 w-4 mr-2" />
                Edit Employment Details
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employment Edit Dialog */}
      <Dialog open={employmentDialogOpen} onOpenChange={setEmploymentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Employment &amp; Salary Details</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Employee ID</Label>
              <div className="rounded-md border border-input bg-muted/40 px-3 py-2 font-mono text-sm">
                {user ? formatEmployeeId(null, user.id) : ""}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="joining_date">
                Joining Date
              </Label>
              <Input
                id="joining_date"
                type="date"
                max={getTodayQatar()}
                value={employmentForm.joining_date}
                onChange={(e) =>
                  setEmploymentForm(f => ({ ...f, joining_date: e.target.value }))
                }
              />
              {employmentErrors.joining_date && (
                <p className="text-xs text-destructive">{employmentErrors.joining_date}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={employmentForm.position}
                onChange={(e) =>
                  setEmploymentForm(f => ({ ...f, position: e.target.value }))
                }
                placeholder="e.g. Senior Developer"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="salary_type">
                Salary Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={employmentForm.salary_type}
                onValueChange={(value) =>
                  setEmploymentForm(f => ({
                    ...f,
                    salary_type: (value === 'hourly' || value === 'monthly')
                      ? value
                      : '',
                  }))
                }
              >
                <SelectTrigger id="salary_type">
                  <SelectValue placeholder="Select salary type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              {employmentErrors.salary_type && (
                <p className="text-xs text-destructive">{employmentErrors.salary_type}</p>
              )}
            </div>

            {canSeeSalaryAmount && (
              <div className="space-y-1.5">
                <Label htmlFor="salary_amount">Salary Amount</Label>
                <Input
                  id="salary_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={employmentForm.salary_amount}
                  onChange={(e) =>
                    setEmploymentForm(f => ({ ...f, salary_amount: e.target.value }))
                  }
                  placeholder="0.00"
                />
                {employmentErrors.salary_amount && (
                  <p className="text-xs text-destructive">{employmentErrors.salary_amount}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Visible only to admin / HR / payroll users.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmploymentDialogOpen(false)}
              disabled={savingEmployment}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEmployment} disabled={savingEmployment}>
              {savingEmployment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Work Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Input
              type="date"
              value={format(dateRange.from, 'yyyy-MM-dd')}
              onChange={e => setDateRange(r => ({ ...r, from: new Date(e.target.value + 'T00:00:00') }))}
              className="w-40"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={format(dateRange.to, 'yyyy-MM-dd')}
              onChange={e => setDateRange(r => ({ ...r, to: new Date(e.target.value + 'T00:00:00') }))}
              className="w-40"
            />
          </div>
          {profileStats.loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="text-2xl font-bold text-green-700">
                  {Math.floor(profileStats.workedMinutes / 60)}h {profileStats.workedMinutes % 60}m
                </div>
                <div className="text-sm text-muted-foreground mt-1">Total Worked</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="text-2xl font-bold text-blue-700">
                  {Math.floor(Math.max(0, profileStats.workedMinutes - profileStats.idleMinutes) / 60)}h {Math.max(0, profileStats.workedMinutes - profileStats.idleMinutes) % 60}m
                </div>
                <div className="text-sm text-muted-foreground mt-1">Active Hours</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="text-2xl font-bold text-orange-700">
                  {Math.floor(profileStats.idleMinutes / 60)}h {profileStats.idleMinutes % 60}m
                </div>
                <div className="text-sm text-muted-foreground mt-1">Idle Hours</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
